import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { btn } from '@/components/ui/styles';
import { requestsInFlight } from '@/utils/http';

/** How often an open tab looks for a new version. */
const CHECK_EVERY_MS = 60_000;

/** A build's own files, as its index.html lists them in the head: the content-hashed script and styles. */
const buildOf = (doc: Document) =>
  [...doc.head.querySelectorAll('script[src], link[href]')]
    .map((el) => el.getAttribute('src') ?? el.getAttribute('href') ?? '')
    .filter((url) => url.startsWith('/assets/') && !url.startsWith('/assets/config/'))
    .sort()
    .join(' ');

/**
 * Whether what someone did may be work to keep: typing or picking anything, or pressing a
 * button, on the page or in its panels. The app's own bars and a page's header (its search
 * and filters, which the URL keeps) do not count.
 */
const holdsWork = (e: Event) => {
  const t = e.target;
  if (!(t instanceof Element) || t.closest('[data-app-chrome], [data-page-header]')) return false;
  return e.type === 'input' || t.closest('button') !== null;
};

/** Reloads once no request is on its way, so a save still going finishes first. */
const reloadWhenIdle = () => {
  if (requestsInFlight() === 0) window.location.reload();
  else window.setTimeout(reloadWhenIdle, 250);
};

/**
 * A new version reaches tabs already open, and nobody loses what they were typing. The
 * shell is never cached (nginx.conf), so a reload always loads the latest build; an open
 * tab looks for one every minute and when it comes back into view. Once there is one, it
 * reloads at a safe moment: on the next page, or while hidden with nothing done on this
 * page. Until then it offers a reload, which asks first when something was done.
 */
const AppUpdate = () => {
  const [ready, setReady] = useState(false);
  const typed = useRef(false);
  const { pathname } = useLocation();
  const opened = useRef(pathname);

  // Look for a new build now and then, and whenever the tab comes back.
  useEffect(() => {
    const running = buildOf(document);
    if (!running) return; // the dev server lists no build files: nothing to compare
    const check = async () => {
      try {
        const res = await fetch('/index.html', { cache: 'no-store' });
        if (!res.ok) return;
        const latest = buildOf(new DOMParser().parseFromString(await res.text(), 'text/html'));
        if (latest && latest !== running) setReady(true);
      } catch {
        // Offline, or mid-deploy: the next check tries again.
      }
    };
    const onShow = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const id = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onShow);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, []);

  // Whether something was done on this page.
  useEffect(() => {
    const onAct = (e: Event) => {
      if (holdsWork(e)) typed.current = true;
    };
    document.addEventListener('input', onAct, true);
    document.addEventListener('click', onAct, true);
    return () => {
      document.removeEventListener('input', onAct, true);
      document.removeEventListener('click', onAct, true);
    };
  }, []);

  // A new page is a safe moment: what the last one held is gone already. It starts with nothing done.
  useEffect(() => {
    if (pathname === opened.current) return;
    opened.current = pathname;
    if (ready) reloadWhenIdle();
    else typed.current = false;
  }, [pathname, ready]);

  // So is a hidden tab with nothing done on its page: nobody is looking at it.
  useEffect(() => {
    if (!ready) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden' && !typed.current) reloadWhenIdle();
    };
    onHide();
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [ready]);

  if (!ready) return null;
  return (
    <div
      role="status"
      data-app-chrome
      className="fixed bottom-4 right-4 z-40 flex max-w-sm animate-pop-in items-center gap-3 rounded-lg bg-surface-raised p-3 text-sm text-fg shadow-e4 max-md:inset-x-4 max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] max-md:max-w-none max-md:animate-sheet-in"
    >
      <p className="min-w-0 flex-1">A new version of Prisma is ready. It loads with your next page, so nothing you are working on is lost.</p>
      <button
        type="button"
        className={classNames(btn.primary, 'shrink-0')}
        onClick={() => {
          if (!typed.current || window.confirm('Something you did on this page may not be saved yet. Reload anyway?')) reloadWhenIdle();
        }}
      >
        Reload now
      </button>
    </div>
  );
};

export default AppUpdate;
